import type { CreateClientResult } from "@/types/api/client";
import type { ApplyStarterPackResult } from "@/types/api/onboarding";

/**
 * Hands the `POST /clients` result from the Create Client page to the
 * Tenant Setup console it navigates to. The result carries fail-open
 * warnings ("no owner holds Admin", starter-pack items that were skipped…)
 * that must stay visible after the navigation, so it is parked in
 * sessionStorage (per tab, survives a refresh) until the admin dismisses it.
 */
const STORAGE_KEY = "inspect-admin:last-client-create-result";

/** Fired on `window` whenever the stored result changes, so the banner re-reads it. */
export const CLIENT_CREATE_RESULT_CHANGED =
	"inspect-admin:client-create-result-changed";

/**
 * Starts of the top-level warnings the starter pack adds to the create
 * result (`ChecklistLibraryService.ApplyStarterPackAsync` and step 6 of
 * `TenantProvisioningService.CreateTenantAsync`). A later successful run
 * from the console supersedes them.
 */
const STARTER_PACK_WARNING_PREFIXES = [
	"No published starter-pack checklists are tagged for industry",
	"Starter pack item failed",
	"Starter pack '",
	"User group '",
	"System role '",
];

export interface StoredClientCreateResult extends CreateClientResult {
	storedAt: string;
}

function notifyChanged(): void {
	window.dispatchEvent(new Event(CLIENT_CREATE_RESULT_CHANGED));
}

export function rememberClientCreateResult(result: CreateClientResult): void {
	try {
		const stored: StoredClientCreateResult = {
			...result,
			storedAt: new Date().toISOString(),
		};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
		notifyChanged();
	} catch {
		// Storage unavailable (private mode / quota) — the console simply
		// shows no banner; the readiness gates still report the same facts.
	}
}

export function readClientCreateResult(
	clientId: string,
): StoredClientCreateResult | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as StoredClientCreateResult;
		if (!parsed?.id || parsed.id.toLowerCase() !== clientId.toLowerCase()) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

export function clearClientCreateResult(): void {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
		notifyChanged();
	} catch {
		// ignore
	}
}

/**
 * A starter-pack run from the console that imported its checklists makes the
 * pack outcome stored at create time stale (typically "No published
 * starter-pack checklists…" when the catalog was filled after the client was
 * created). Drops that part of the stored result; the Starter pack card shows
 * the new run. When no other warning is left the banner is cleared.
 */
export function supersedeStarterPackInClientCreateResult(
	clientId: string,
	pack: ApplyStarterPackResult,
): void {
	if (pack.items.length === 0 || pack.items.some((item) => item.failed)) {
		return;
	}
	const stored = readClientCreateResult(clientId);
	if (!stored) return;

	const warnings = (stored.warnings ?? []).filter(
		(warning) =>
			!STARTER_PACK_WARNING_PREFIXES.some((prefix) =>
				warning.startsWith(prefix),
			),
	);
	if (warnings.length === 0) {
		clearClientCreateResult();
		return;
	}
	try {
		const updated: StoredClientCreateResult = {
			...stored,
			// Same wording as Admin/Clients/Create, recounted.
			message: `Client '${stored.clientName}' created with ${warnings.length} warning(s).`,
			warnings,
			starterPack: [],
			starterPackUserGroups: [],
		};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
		notifyChanged();
	} catch {
		// ignore
	}
}
